import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { UserNamePipeModule } from '@wilson/account';
import { UserQualificationV2 } from '@wilson/interfaces';
import {
  CreateUserQualificationDto,
  QualificationsV2MultiUsersSelectComponent,
  QualificationV2SelectComponent,
  UserQualificationsGateway,
} from '@wilson/qualification-v2/common';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
  tap,
  timer,
} from 'rxjs';
import { UserQualificationsV2AttachmentGatewayService } from '../../../../gateways/user-qualifications-v2-attachment-gateway.service';
import { BulkQualificationsAttachmentSectionComponent } from '../../shared-drawer-components/bulk-qualifications-attachment-section/bulk-qualifications-attachment-section.component';
import { BulkQualificationsDatesAndApprovalSectionComponent } from '../../shared-drawer-components/bulk-qualifications-dates-and-approval-section/bulk-qualifications-dates-and-approval-section.component';
import { BulkQualificationsFooterSectionComponent } from '../../shared-drawer-components/bulk-qualifications-footer-section/bulk-qualifications-footer-section.component';
import { BulkQualificationsNotesSectionComponent } from '../../shared-drawer-components/bulk-qualifications-notes-section/bulk-qualifications-notes-section.component';
import { BulkCreateQualificationsService } from '../bulk-create-qualifications.service';

@Component({
  selector: 'wilson-bulk-create-qualifications-drawer',
  imports: [
    BulkQualificationsAttachmentSectionComponent,
    BulkQualificationsDatesAndApprovalSectionComponent,
    BulkQualificationsFooterSectionComponent,
    BulkQualificationsNotesSectionComponent,
    CommonModule,
    FormsModule,
    NzButtonModule,
    NzCollapseModule,
    NzDatePickerModule,
    NzDrawerModule,
    NzFormModule,
    NzGridModule,
    NzIconModule,
    NzInputModule,
    NzListModule,
    NzSelectModule,
    NzSwitchModule,
    ReactiveFormsModule,
    TranslatePipe,
    UserNamePipeModule,
    FontAwesomeModule,
    QualificationsV2MultiUsersSelectComponent,
    QualificationV2SelectComponent,
  ],
  templateUrl: './bulk-create-qualifications-drawer.component.html',
  styleUrl: './bulk-create-qualifications-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BulkCreateQualificationsDrawerComponent
  implements OnInit, OnDestroy
{
  private readonly bulkCreateQualificationsService = inject(
    BulkCreateQualificationsService,
  );
  private readonly userQualificationsGateway = inject(
    UserQualificationsGateway,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);
  private readonly userQualificationsV2AttachmentGatewayService = inject(
    UserQualificationsV2AttachmentGatewayService,
  );

  private destroyed$ = new Subject<void>();

  protected savingDrawer$ = new BehaviorSubject<boolean>(false);
  protected bulkCreateDrawerVisible$!: BulkCreateQualificationsService['bulkCreateDrawerVisible$'];

  protected userIdsFormControl = new FormControl<string[]>([]);
  protected qualificationFormControl = new FormControl<string[]>([]);
  protected acquiredAtFormControl = new FormControl<Date | null>(null, [
    Validators.required,
  ]);
  protected validFromFromControl = new FormControl<Date | null>(null, [
    Validators.required,
  ]);
  protected validUntilFormControl = new FormControl<Date | null>(null, [
    Validators.required,
  ]);
  protected infiniteValidFormControl = new FormControl<boolean>(false);
  protected confirmedByEmployeeFormControl = new FormControl<boolean>(false);
  protected notesFormControl = new FormControl<string>('');

  protected userIdList: string[] = [];
  protected selectedQualificationCategoryFilterId!: string | null;
  protected attachmentsList: NzUploadFile[] = [];

  readonly faTrashAlt = faTrashAlt;

  ngOnInit(): void {
    this.bulkCreateDrawerVisible$ =
      this.bulkCreateQualificationsService.bulkCreateDrawerVisible$;
    this.setupSavingDrawerListener();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public closeBulkCreateDrawer(): void {
    this.bulkCreateQualificationsService.setBulkCreateDrawerVisible(false);
  }

  public cancelDrawer(): void {
    this.resetDrawer();
    this.closeBulkCreateDrawer();
  }

  public saveDrawer(): void {
    this.savingDrawer$.next(true);
    const bulkDto: CreateUserQualificationDto[] = [];
    this.userIdList.forEach((userId) => {
      (this.qualificationFormControl.value ?? []).forEach((qualificationId) => {
        bulkDto.push({
          userId: userId,
          qualificationId: qualificationId,
          acquiredAt:
            this.acquiredAtFormControl.value?.toISOString() ||
            new Date().toISOString(),
          validFrom:
            this.validFromFromControl.value?.toISOString() ||
            new Date().toISOString(),
          validTill:
            this.validUntilFormControl.value?.toISOString() ||
            new Date().toISOString(),
          confirmedByUser: this.confirmedByEmployeeFormControl.value || false,
          notes: this.notesFormControl.value,
        });
      });
    });
    this.userQualificationsGateway
      .createUserQualifications(bulkDto)
      .pipe(
        take(1),
        tap((createdUserQualifications) => {
          if (
            createdUserQualifications?.length &&
            this.attachmentsList.length
          ) {
            this.addAttachmentsToCreatedQualifications(
              createdUserQualifications,
            );
          } else {
            this.handleSuccessfulDrawerSave();
          }
        }),
        catchError((error) => {
          return this.handleFailedDrawerSave(error);
        }),
      )
      .subscribe();
  }

  public handleUserSelection(selectedIds: string[]): void {
    this.userIdList = selectedIds;
  }

  public removeUser(userIdToRemove: string): void {
    const index = this.userIdList.indexOf(userIdToRemove);
    if (index !== -1) {
      this.userIdList.splice(index, 1);
    }
  }

  private resetDrawer(): void {
    this.userIdsFormControl.reset([]);
    this.userIdList = [];
    this.selectedQualificationCategoryFilterId = null;
    this.qualificationFormControl.reset([]);
    this.acquiredAtFormControl.reset(null);
    this.validFromFromControl.reset(null);
    this.validUntilFormControl.reset(null);
    this.infiniteValidFormControl.reset(false);
    this.confirmedByEmployeeFormControl.reset(false);
    this.attachmentsList = [];
    this.notesFormControl.reset('');
  }

  private displayConflictError(): void {
    this.nzMessageService.error(
      this.translateService.instant(
        'user_qualification.create.error.already_exists',
      ),
    );
  }

  private displayGenericError(): void {
    this.nzMessageService.error(this.translateService.instant('general.error'));
  }

  private displaySuccessMessage(): void {
    this.nzMessageService.success(
      this.translateService.instant('general.saved_successfully'),
    );
  }

  private handleFailedDrawerSave(error?: any): Observable<undefined> {
    if (error && error?.error?.statusCode === 409) {
      this.displayConflictError();
    } else {
      this.displayGenericError();
    }

    timer(500)
      .pipe(take(1))
      .subscribe(() => {
        this.savingDrawer$.next(false);
      });
    return of(undefined);
  }

  private handleSuccessfulDrawerSave(): void {
    this.displaySuccessMessage();
    this.bulkCreateQualificationsService.triggerBulkCreatedQualifications();
    this.resetDrawer();
    this.closeBulkCreateDrawer();
    this.savingDrawer$.next(false);
  }

  private addAttachmentsToCreatedQualifications(
    createdUserQualifications: Pick<UserQualificationV2, 'id'>[],
  ): void {
    this.userQualificationsV2AttachmentGatewayService
      .addAttachments(
        this.attachmentsList,
        createdUserQualifications.map((q) => q.id),
      )
      .pipe(
        take(1),
        tap(() => {
          this.handleSuccessfulDrawerSave();
        }),
        catchError(() => {
          this.handleFailedDrawerSave();
          return of(undefined);
        }),
      )
      .subscribe();
  }

  private setupSavingDrawerListener(): void {
    this.savingDrawer$
      .pipe(
        takeUntil(this.destroyed$),
        tap((saving) => {
          if (saving) {
            this.userIdsFormControl.disable();
            this.qualificationFormControl.disable();
            this.acquiredAtFormControl.disable();
            this.validFromFromControl.disable();
            this.validUntilFormControl.disable({
              emitEvent: false,
            });
            this.infiniteValidFormControl.disable({
              emitEvent: false,
            });
            this.confirmedByEmployeeFormControl.disable();
            this.notesFormControl.disable();
          } else {
            this.userIdsFormControl.enable();
            this.qualificationFormControl.enable();
            this.acquiredAtFormControl.enable();
            this.validFromFromControl.enable();
            this.validUntilFormControl.enable({
              emitEvent: false,
            });
            this.infiniteValidFormControl.enable({
              emitEvent: false,
            });
            this.confirmedByEmployeeFormControl.enable();
            this.notesFormControl.enable();
          }
        }),
      )
      .subscribe();
  }
}
